// Partners — current ground truth for the DEMAND side.
//
// Sourced from three documents that landed in the 2 Jun 2026 review cycle
// and supersede the older data-anchored 9-category taxonomy on /demand:
//
//   - "Bvndle Redemption_Partners_Report End April 2026.docx" — the master
//     merchant directory; per-merchant onboarding status + reach + branches
//   - "1 pager_Bvndle Merchant LaaS Update Report.docx" (2 Jun 2026) — YTD
//     redemption numbers per merchant (Jan–May 2026), category completion %,
//     headline totals (398,686 coins YTD across 20 active merchants)
//   - "Bvndle_LaaS_Pilot_Readiness_Assessment_v5.html" — issuer-pilot
//     readiness (MTN + UBA); lives in src/lib/issuer-pilots.ts, not here
//
// This file owns the 11 partner categories and the ~40 merchants underneath
// them. Render flow planned: category → click → merchant list → click →
// per-merchant work surface (facts + contact + commitment editor).
//
// Refresh cadence: whenever the partners report / 1-pager move, update
// this file. The Worker layer (Ask / Knowledge) picks up the change on
// the next desktop-app rebuild.

// ---------------------------------------------------------------------------
// Status vocabulary — exact match to the partners-report legend so the
// chairman sees the same symbols he sees in the source .docx.
// ---------------------------------------------------------------------------

export type MerchantStatus =
  | "onboarded" //          ● Fully onboarded, live on the Bvndle platform
  | "integrating" //        ◕ Integrating
  | "mgmt-alignment" //     ◑ Mgmt. alignment / proposal sent
  | "reviewing-sla" //      ◐ Reviewing SLA
  | "value-prop" //         ○ Value-proposition phase
  | "prospect"; //          ◌ Prospect, not yet engaged

export const STATUS_ICON: Record<MerchantStatus, string> = {
  "onboarded": "●",
  "integrating": "◕",
  "mgmt-alignment": "◑",
  "reviewing-sla": "◐",
  "value-prop": "○",
  "prospect": "◌",
};

export const STATUS_LABEL: Record<MerchantStatus, string> = {
  "onboarded": "Onboarded",
  "integrating": "Integrating",
  "mgmt-alignment": "Mgmt. Alignment",
  "reviewing-sla": "Reviewing SLA",
  "value-prop": "Value Prop",
  "prospect": "Prospect",
};

/** Hex-ish color per status — tuned for both light (chairman docs) and
 * dark (in-app) surfaces. */
export const STATUS_COLOR: Record<MerchantStatus, string> = {
  "onboarded": "#2d8a4f",
  "integrating": "#5ec98c",
  "mgmt-alignment": "#d99c3a",
  "reviewing-sla": "#c47a2b",
  "value-prop": "#7a7a8a",
  "prospect": "#b3b3b3",
};

// ---------------------------------------------------------------------------
// Merchant + Category shapes
// ---------------------------------------------------------------------------

export type Month = "jan" | "feb" | "mar" | "apr" | "may";

export interface MonthlyRedemption {
  jan?: number;
  feb?: number;
  mar?: number;
  apr?: number;
  may?: number;
}

export interface Merchant {
  /** Stable id derived from name. */
  id: string;
  /** Display name as it appears in the partners report. */
  name: string;
  /** Category code (matches PARTNER_CATEGORIES[].code). */
  category: string;
  status: MerchantStatus;
  /** Geography label: Lagos · Regional · National · Online · Global · etc. */
  reach: string;
  /** Total physical branches the merchant operates. null = online-only / unknown. */
  totalBranches: number | null;
  /** Branches currently live on Bvndle. null = none / unknown. */
  onboardedBranches: number | null;
  /** True for merchants from the standard (non-priority) supplementary list. */
  standard?: boolean;
  /** Monthly coin redemption (Jan–May 2026, from the 1-pager). */
  monthly?: MonthlyRedemption;
  /** YTD total in coins. Computed from monthly when present. */
  ytdTotal?: number;
  // ----- Work-surface fields (populated by the team in the editor; empty by default) -----
  contact?: { name?: string; email?: string; phone?: string };
  bvndleOwner?: string;
  lastTouch?: { at: string; note: string };
  blockers?: string;
  doing?: string;
}

export interface PartnerCategory {
  /** Stable code used in action impact paths (d1:supermarkets etc.). */
  code: string;
  label: string;
  description: string;
  /** True for the PRIMARY anchor (Supermarkets per the report). */
  primary?: boolean;
  /** Number of merchants the team is committed to onboard per category. */
  targetOnboarded: number;
  /** Optional Bvndle-side category lead (best guess from trackers). */
  lead?: string;
}

// ---------------------------------------------------------------------------
// 11 partner categories
// ---------------------------------------------------------------------------

export const PARTNER_CATEGORIES: PartnerCategory[] = [
  {
    code: "supermarkets",
    label: "Retail · Supermarkets",
    description: "PRIMARY — everyday essential spending anchor",
    primary: true,
    targetOnboarded: 3,
    lead: "Williams · Ufuoma (high-redemption status)",
  },
  {
    code: "automotive",
    label: "Automotive · Mobility",
    description: "Fuel & transport — high-frequency utility",
    targetOnboarded: 3,
    lead: "Williams · Ruki (BvndleGo)",
  },
  {
    code: "health",
    label: "Health & Wellness",
    description: "Fitness, pharmacy, personal care services",
    targetOnboarded: 3,
    lead: "Seye (Medplus LaaS) · Williams",
  },
  {
    code: "dining",
    label: "Dining · Restaurants",
    description: "Food & beverage experiences",
    targetOnboarded: 3,
    lead: "Williams · CX + PSL (Glovo)",
  },
  {
    code: "beauty",
    label: "Beauty & Personal Care",
    description: "Lifestyle grooming & beauty retail",
    targetOnboarded: 3,
    lead: "Williams",
  },
  {
    code: "ecommerce",
    label: "E-Commerce · Digital Services",
    description: "Online platforms with broad reach",
    targetOnboarded: 3,
    lead: "Williams · PSL (LaaS)",
  },
  {
    code: "tech",
    label: "Technology & Electronics",
    description: "Consumer tech & electronics retail",
    targetOnboarded: 3,
    lead: "Williams",
  },
  {
    code: "hospitality",
    label: "Hospitality & Travel",
    description: "Hotels, serviced apartments & travel",
    targetOnboarded: 3,
    lead: "Williams · Ayo (Amber Hospitality deck in review)",
  },
  {
    code: "fashion",
    label: "Fashion & Accessories",
    description: "Apparel & lifestyle fashion retail",
    targetOnboarded: 3,
    lead: "Williams",
  },
  {
    code: "entertainment",
    label: "Entertainment & Experiences",
    description: "Cinema, leisure & social experiences",
    targetOnboarded: 3,
    lead: "Williams · Seye (Filmhouse)",
  },
  {
    code: "gifting",
    label: "Gifting",
    description: "Flowers, gifts & occasion retail",
    targetOnboarded: 3,
    lead: "Williams (Bvndle Gift Cards strategy)",
  },
];

// ---------------------------------------------------------------------------
// Master merchant list. Priority merchants per the End-April Partners Report,
// supplemented with standard merchants and YTD figures from the 1-pager
// where they match. Where the 1-pager and the report disagree on a name
// spelling, the 1-pager wins (it's two months newer).
// ---------------------------------------------------------------------------

function totalYtd(m: MonthlyRedemption | undefined): number {
  if (!m) return 0;
  return (m.jan ?? 0) + (m.feb ?? 0) + (m.mar ?? 0) + (m.apr ?? 0) + (m.may ?? 0);
}

function merchant(
  id: string,
  name: string,
  category: string,
  status: MerchantStatus,
  reach: string,
  totalBranches: number | null,
  onboardedBranches: number | null,
  monthly?: MonthlyRedemption,
  extra?: Partial<Merchant>,
): Merchant {
  return {
    id,
    name,
    category,
    status,
    reach,
    totalBranches,
    onboardedBranches,
    monthly,
    ytdTotal: totalYtd(monthly),
    ...extra,
  };
}

export const MERCHANTS: Merchant[] = [
  // --- SUPERMARKETS ---
  merchant("jendol", "Jendol Superstores", "supermarkets", "onboarded", "Regional", 12, 10,
    { apr: 64_760, may: 2_871 }), // YTD 67,631
  merchant("market-square", "Market Square (Purple Mall Lekki)", "supermarkets", "onboarded", "National", 38, 2,
    { apr: 1_000 }),
  merchant("trolleys", "Trolleys Supermarket", "supermarkets", "onboarded", "National", 1, 1,
    { mar: 1_665, apr: 1_800, may: 4_183 }),
  merchant("legend", "Legend Supermarket", "supermarkets", "reviewing-sla", "Lagos", 1, 0),
  merchant("justrite", "Justrite", "supermarkets", "value-prop", "Regional", 37, 0),
  merchant("home-affairs", "Home Affairs", "supermarkets", "value-prop", "Regional", 4, 0),
  merchant("grandsquare", "Grandsquare", "supermarkets", "value-prop", "National", 4, 0),
  merchant("foodco", "FoodCo", "supermarkets", "value-prop", "Regional", 23, 0),

  // --- AUTOMOTIVE / MOBILITY ---
  merchant("nepal", "Nepal Energies (Akowonjo)", "automotive", "onboarded", "National", 35, 1,
    { may: 22_684 }),
  merchant("bvndlego", "BvndleGo", "automotive", "onboarded", "Regional", null, null),
  merchant("ardova", "Ardova Plc", "automotive", "value-prop", "National", 450, 0,
    undefined, { doing: "Pre-funded model proof-point; replicate across priority categories" }),
  merchant("total-energies", "Total Energies", "automotive", "value-prop", "National", 200, 0),
  merchant("eterna", "Eterna Filling Station", "automotive", "prospect", "National", 250, 0),
  merchant("edryv", "Edryv", "automotive", "integrating", "Nationwide", null, null,
    undefined, { doing: "Integrating · 13,000 users" }),

  // --- HEALTH & WELLNESS ---
  merchant("i-fitness", "i-Fitness", "health", "onboarded", "National", 30, 29,
    { mar: 0, apr: 4_288 }),
  merchant("ace-beauty-bar", "Ace Beauty Bar", "health", "onboarded", "Lagos", 1, 1,
    { mar: 3_500 }),
  merchant("medplus", "Medplus", "health", "mgmt-alignment", "National", 110, 0,
    undefined, { blockers: "Stuck at mgmt-alignment — blocks 110-branch pharmacy depth" }),

  // --- DINING / RESTAURANTS ---
  merchant("yellow-chilli", "Yellow Chilli", "dining", "onboarded", "Lagos", 2, 2),
  merchant("el-padrino", "El Padrino Restaurant Limited", "dining", "onboarded", "Lagos", 2, 2,
    { jan: 8_619, feb: 5_222, mar: 16_973, apr: 1_440, may: 74_270 }), // YTD 106,524 — top redeemer
  merchant("burger-king", "Burger King", "dining", "reviewing-sla", "National", 16, 0),
  merchant("olive-panarottis", "Olive Family / Panarottis", "dining", "mgmt-alignment", "National", 12, 0,
    undefined, { lastTouch: { at: "2026-05", note: "Proposal sent" } }),
  merchant("tastee", "Tastee Fried Chicken", "dining", "value-prop", "Regional", 14, 0),
  merchant("chicken-republic", "Chicken Republic", "dining", "value-prop", "National", 200, 0),
  merchant("kilimanjaro", "Kilimanjaro", "dining", "value-prop", "National", 90, 0),
  merchant("debonairs", "Debonairs Pizza", "dining", "value-prop", "National", 9, 0),
  merchant("item-7", "Item 7", "dining", "prospect", "Regional", 18, 0),
  merchant("ofada-hut", "Ofada Hut", "dining", "onboarded", "Lagos", 2, 2,
    { feb: 2_800, mar: 31_940, apr: 11_680, may: 7_570 }, { standard: true }),
  merchant("yoko-foods", "Yoko Foods", "dining", "onboarded", "Lagos", 2, 2, undefined, { standard: true }),
  merchant("the-beanry", "The Beanry", "dining", "onboarded", "Lagos", 1, 1, undefined, { standard: true }),
  merchant("rubels-angels", "Rubels and Angels", "dining", "value-prop", "Lagos", 5, 0, undefined, { standard: true }),
  merchant("e-bar-metro", "E BAR Metro", "dining", "onboarded", "Lagos", null, null,
    { jan: 8_700, mar: 4_494 }, { standard: true }),
  merchant("cellar-central", "Cellar Central Limited", "dining", "onboarded", "Lagos", null, null,
    { jan: 8_326, feb: 100 }, { standard: true }),

  // --- BEAUTY & PERSONAL CARE ---
  merchant("dang", "Dang Lifestyle Ikeja", "beauty", "onboarded", "National", 7, 7,
    { may: 5_200 }),
  merchant("massage-camel", "Massage by Camel", "beauty", "onboarded", "Regional", null, null),
  merchant("the-fragrance", "The Fragrance Lagos Limited", "beauty", "onboarded", "National", 1, 1,
    { apr: 7_200 }),
  merchant("cnm-essentials", "CNM Essentials and Utilities Limited", "beauty", "onboarded", "Lagos", 1, 1,
    { apr: 1_490 }),
  merchant("montaigne", "The Montaigne Place", "beauty", "reviewing-sla", "National", 36, 0),
  merchant("ginger", "Ginger", "beauty", "reviewing-sla", "National", null, null,
    undefined, { reach: "National (online)" } as Partial<Merchant>),
  merchant("oriki", "Oriki Spa", "beauty", "value-prop", "West Africa", 12, 0),
  merchant("hair-house", "Hair House Nigeria", "beauty", "prospect", "National", 1, 0),
  merchant("ginger-me", "Ginger Me", "beauty", "onboarded", "National", null, null, undefined, { standard: true }),

  // --- E-COMMERCE / DIGITAL SERVICES ---
  merchant("pricepally", "Pricepally Nigeria Limited", "ecommerce", "onboarded", "Global · online", null, null,
    { may: 18_600 }),
  merchant("konga", "Konga", "ecommerce", "onboarded", "National · online", null, null),

  // --- TECHNOLOGY & ELECTRONICS ---
  merchant("festajei", "Festajei Multiservices", "tech", "onboarded", "Regional", 3, 3,
    { may: 1_000 }),
  merchant("garamart", "Garamart", "tech", "onboarded", "National · online", null, null,
    { may: 3_909 }),
  merchant("sims", "SIMS", "tech", "prospect", "National", 18, 0),
  merchant("electromart", "Electromart", "tech", "prospect", "National", 30, 0),
  merchant("slot", "Slot", "tech", "prospect", "National", 80, 0),

  // --- HOSPITALITY & TRAVEL ---
  merchant("boardroom", "Jotani-Boardroom Apartment", "hospitality", "onboarded", "Regional", 1, 1,
    { mar: 2_950, apr: 150 }),
  merchant("geo-travels", "Geo Travels", "hospitality", "onboarded", "National", null, null),
  merchant("delborough", "Delborough", "hospitality", "value-prop", "Regional", 1, 0),
  merchant("presken", "Presken Hotel", "hospitality", "value-prop", "National", 18, 0),
  merchant("355-lounges", "355 Lounges / Hotels", "hospitality", "prospect", "Regional", null, null),

  // --- FASHION & ACCESSORIES ---
  merchant("4ey", "4ey Fashion", "fashion", "onboarded", "National", 1, 1,
    { apr: 4_800, may: 23_500 }),
  merchant("ruff-tumble", "Ruff n Tumble", "fashion", "value-prop", "National", null, null),
  merchant("twice-as-nice", "Twice As Nice", "fashion", "value-prop", "Regional", null, null),
  merchant("essenza", "Essenza", "fashion", "prospect", "Regional", null, null),

  // --- ENTERTAINMENT & EXPERIENCES ---
  merchant("carven", "Carven", "entertainment", "onboarded", "Regional", null, null),
  merchant("breather", "Breather Renaissance", "entertainment", "onboarded", "Regional", 1, 1,
    { apr: 3_000 }),
  merchant("film-house", "Film House", "entertainment", "integrating", "National", 12, 0,
    undefined, { blockers: "Filmhouse integration BLOCKED — 12-branch unlock waiting" }),
  merchant("rufus-bees", "Rufus and Bees", "entertainment", "value-prop", "Regional", 1, 0),
  merchant("ebonylife", "Ebonylife Place", "entertainment", "prospect", "Regional", 1, 0),
  merchant("herel-play", "Herel Play", "entertainment", "onboarded", "Regional", null, null,
    { jan: 13_585, feb: 12_392, mar: 10_189, apr: 4_689, may: 1_125 }, // YTD 41,980
    { standard: true }),
  merchant("purelife", "Purelife Pharmacy Hakeem Dickson", "entertainment", "onboarded", "Lagos", 1, 1,
    { apr: 310 }, { standard: true }),

  // --- GIFTING ---
  merchant("regal-flowers", "Regal Flowers", "gifting", "onboarded", "National", 2, 2),
  merchant("scribbles", "Scribbles", "gifting", "onboarded", "Online", null, null),
];

// ---------------------------------------------------------------------------
// Headline numbers (from the 1-pager, 2 Jun 2026)
// ---------------------------------------------------------------------------

export const HEADLINE = {
  /** Merchants on the priority + standard target list combined. */
  merchantsTargeted: 33,
  /** Merchants actively redeeming YTD. */
  activeMerchants: 20,
  /** Total YTD coin redemption, Jan–May 2026. */
  ytdRedemption: 398_686,
  /** Priority merchant share of YTD value. */
  priorityShareCoins: 290_286, // 73% of 398,686
  /** Overall onboarding progress across the 33-merchant target. */
  onboardingCompletion: 0.70,
} as const;

// ---------------------------------------------------------------------------
// Rollups
// ---------------------------------------------------------------------------

export interface CategoryRollup {
  category: PartnerCategory;
  merchants: Merchant[];
  priority: Merchant[];
  standard: Merchant[];
  onboardedCount: number;
  pipelineCount: number;
  /** Onboarded / target — 1.0 = at target, >1 over. */
  completion: number;
  /** Sum of YTD coins across merchants in this category. */
  ytdRedemption: number;
  /** Top redeemer this YTD, if any. */
  topRedeemer: Merchant | null;
}

export function rollupCategory(catCode: string): CategoryRollup {
  const cat = PARTNER_CATEGORIES.find((c) => c.code === catCode);
  if (!cat) throw new Error(`Unknown category ${catCode}`);
  const ms = MERCHANTS.filter((m) => m.category === catCode);
  const priority = ms.filter((m) => !m.standard);
  const standard = ms.filter((m) => m.standard);
  const onboarded = ms.filter((m) => m.status === "onboarded");
  const pipeline = ms.filter((m) => m.status !== "onboarded");
  let ytd = 0;
  let top: Merchant | null = null;
  for (const m of ms) {
    const t = m.ytdTotal ?? 0;
    ytd += t;
    if (t > (top?.ytdTotal ?? 0)) top = m;
  }
  return {
    category: cat,
    merchants: ms,
    priority,
    standard,
    onboardedCount: onboarded.length,
    pipelineCount: pipeline.length,
    completion: onboarded.length / cat.targetOnboarded,
    ytdRedemption: ytd,
    topRedeemer: top,
  };
}

export function rollupAll(): CategoryRollup[] {
  return PARTNER_CATEGORIES.map((c) => rollupCategory(c.code));
}

/** Share-of-total redemption per category — drives the TODAY stacked bar
 * on the demand surfaces. */
export function categoryRedemptionShare(): Array<{
  category: PartnerCategory;
  ytd: number;
  share: number;
}> {
  const rollups = rollupAll();
  const total = rollups.reduce((acc, r) => acc + r.ytdRedemption, 0);
  return rollups
    .map((r) => ({
      category: r.category,
      ytd: r.ytdRedemption,
      share: total > 0 ? r.ytdRedemption / total : 0,
    }))
    .sort((a, b) => b.share - a.share);
}
